import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal } from 'lucide-react';
import { useExpenses } from '@/composables/expense/useExpenses';
import { useExpenseCategories } from '@/composables/expense/useExpenseCategories';
import { useAccounts } from '@/composables/financial/useAccounts';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { DATE_PRESETS, rangeForPreset } from '@/lib/dateRange';
import { useState, useMemo } from 'react';
import type { Expense } from '@/types/table';

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'REIMBURSED', label: 'Reimbursed' },
];

export function ExpenseListPage() {
  const navigate = useNavigate();
  const { expenses, meta, isLoading, filters, exportData, submitExpense, deleteExpense } = useExpenses();
  const { accounts } = useAccounts();
  const { categories } = useExpenseCategories();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [exp, setExp] = useState({ format: 'csv', accountId: '', status: '', categoryId: '', dateFrom: '', dateTo: '', minAmount: '', maxAmount: '' });

  const handleDatePreset = (preset: string) => {
    if (preset === 'custom') { filters.set({ datePreset: 'custom' }); return; }
    const { dateFrom, dateTo } = rangeForPreset(preset);
    filters.set({ datePreset: preset, dateFrom, dateTo });
  };

  // Seed the export dialog from the filters already applied on the page; if no
  // date is applied, default to the trailing year.
  const openExport = () => {
    const f = filters.current;
    const seed = (f.dateFrom || f.dateTo) ? { dateFrom: f.dateFrom, dateTo: f.dateTo } : rangeForPreset('last_year');
    setExp({
      format: 'csv',
      accountId: f.accountId || '',
      status: f.status || '',
      categoryId: f.categoryId || '',
      dateFrom: seed.dateFrom,
      dateTo: seed.dateTo,
      minAmount: f.minAmount || '',
      maxAmount: f.maxAmount || '',
    });
    setShowExport(true);
  };

  const handleExport = () => {
    const params: Record<string, unknown> = { sortBy: 'expenseDate', sortOrder: 'asc' };
    if (exp.accountId) params.accountId = exp.accountId;
    if (exp.status) params.status = exp.status;
    if (exp.categoryId) params.categoryId = exp.categoryId;
    if (exp.dateFrom) params.dateFrom = exp.dateFrom;
    if (exp.dateTo) params.dateTo = exp.dateTo;
    if (exp.minAmount) params.minAmount = exp.minAmount;
    if (exp.maxAmount) params.maxAmount = exp.maxAmount;
    const ext = exp.format === 'xlsx' ? 'xlsx' : exp.format === 'pdf' ? 'pdf' : 'csv';
    const span = [exp.dateFrom, exp.dateTo].filter(Boolean).join('_to_');
    exportFile(() => exportData(exp.format, params), `expenses${span ? '_' + span : ''}.${ext}`);
    setShowExport(false);
  };

  const columns: ColumnDef<Expense, unknown>[] = useMemo(() => [
    { accessorKey: 'title', header: ({ column }) => <DataTableColumnHeader column={column} title="Title" />, cell: ({ row }) => <span className="font-medium">{row.original.title}</span> },
    { accessorKey: 'amount', header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />, cell: ({ row }) => formatCurrency(row.original.amount, row.original.currency) },
    { accessorKey: 'category', header: 'Category', cell: ({ row }) => row.original.category?.name || '-' },
    { accessorKey: 'account', header: 'Account', cell: ({ row }) => row.original.account?.name || '-' },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} type="expense" /> },
    { accessorKey: 'expenseDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />, cell: ({ row }) => formatDate(row.original.expenseDate) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/expenses/${row.original.id}`); }}>View</DropdownMenuItem>
            {row.original.status === 'DRAFT' && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); submitExpense.mutate(row.original.id); }}>Submit</DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Expenses" description="Track and manage expenses" actions={<><Button variant="outline" onClick={openExport} disabled={isExporting}><Download className="mr-2 h-4 w-4" />Export</Button><Button onClick={() => navigate('/expenses/new')}><Plus className="mr-2 h-4 w-4" />New Expense</Button></>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search title or merchant..." filters={
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filters.current.accountId || 'all'} onValueChange={(v) => filters.set({ accountId: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-44"><SelectValue placeholder="All Accounts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accounts</SelectItem>
              {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.current.status || 'all'} onValueChange={(v) => filters.set({ status: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.current.categoryId || 'all'} onValueChange={(v) => filters.set({ categoryId: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-44"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.current.datePreset} onValueChange={handleDatePreset}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {filters.current.datePreset === 'custom' && (
            <>
              <Input type="date" aria-label="From date" value={filters.current.dateFrom} onChange={(e) => filters.set({ dateFrom: e.target.value })} className="w-40" />
              <Input type="date" aria-label="To date" value={filters.current.dateTo} onChange={(e) => filters.set({ dateTo: e.target.value })} className="w-40" />
            </>
          )}
          <Input type="number" placeholder="Min amount" value={filters.current.minAmount} onChange={(e) => filters.set({ minAmount: e.target.value })} className="w-32" />
          <Input type="number" placeholder="Max amount" value={filters.current.maxAmount} onChange={(e) => filters.set({ maxAmount: e.target.value })} className="w-32" />
        </div>
      } />
      <DataTable columns={columns} data={expenses} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/expenses/${row.id}`)} />

      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Expenses</DialogTitle>
            <DialogDescription>Downloads the selected range and filters. Pre-filled from the filters on the page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Format</Label>
              <Select value={exp.format} onValueChange={(v) => setExp(s => ({ ...s, format: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (.csv)</SelectItem>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                  <SelectItem value="pdf">PDF (.pdf)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Account</Label>
              <Select value={exp.accountId || 'all'} onValueChange={(v) => setExp(s => ({ ...s, accountId: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={exp.status || 'all'} onValueChange={(v) => setExp(s => ({ ...s, status: v === 'all' ? '' : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={exp.categoryId || 'all'} onValueChange={(v) => setExp(s => ({ ...s, categoryId: v === 'all' ? '' : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>From</Label><Input type="date" value={exp.dateFrom} onChange={(e) => setExp(s => ({ ...s, dateFrom: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>To</Label><Input type="date" value={exp.dateTo} onChange={(e) => setExp(s => ({ ...s, dateTo: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Min amount</Label><Input type="number" placeholder="Any" value={exp.minAmount} onChange={(e) => setExp(s => ({ ...s, minAmount: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Max amount</Label><Input type="number" placeholder="Any" value={exp.maxAmount} onChange={(e) => setExp(s => ({ ...s, maxAmount: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExport(false)}>Cancel</Button>
            <Button onClick={handleExport} disabled={isExporting}><Download className="mr-2 h-4 w-4" />{isExporting ? 'Exporting…' : 'Export'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Expense"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteExpense.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteExpense.isPending}
      />
    </PageContainer>
  );
}
